// WHAT: Admin-Dashboard-Widget, das für mehrere Qualitätsmetriken jeweils die „schlechtesten“ Dateien hervorhebt
// WHY:  Unterstützt Architektur- und Refactoring-Entscheidungen, indem es Hotspots bei Kommentardichte, Modularität, Komplexität und Issues anzeigt
// DOMAIN: core → dashboard → widgets → admin
// DEPENDENCIES: `useCodeQualityMatrix`-Hook, `WidgetContainer`, TracQ `Badge`, lucide-react Icons, WIDGET_IDS-Registry
// RISKS:
//   - NOTE: Erwartet, dass `data.worstFilesByMetric` und `data.loading/errors` für alle Metriken konsistent befüllt sind
//   - NOTE: Severity-Zuordnung (`critical/high/…`) ist heuristisch und muss mit Analyzer-/Backend-Logik abgestimmt bleiben

import { FileCode, AlertTriangle, TrendingDown, Bug, Clock } from "lucide-react";
import { type FC, useMemo } from "react";

import { Badge } from "@/components/ui";
import { cn } from "@/shared/utils";

import { useCodeQualityMatrix } from "../../../hooks/useCodeQualityMatrix";
import { useWidgetConfig } from "../../../hooks/useWidgetConfig";
import { WIDGET_IDS } from "../../../store/widgetRegistry";
import { WidgetContainer } from "../base/WidgetContainer";

/**
 * Get severity color classes for code quality severity
 *
 * @param severity - Severity level
 *
 * @returns {string} CSS classes for severity color
 *
 */
const getSeverityColor = (severity?: string): string => {
  switch (severity) {
    case "critical": {
      return "bg-red-500/20 text-red-400 border-red-500/30";
    }
    case "high": {
      return "bg-orange-500/20 text-orange-400 border-orange-500/30";
    }
    case "medium": {
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    }
    case "low": {
      return "bg-green-500/20 text-green-400 border-green-500/30";
    }
    default: {
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  }
};

/**
 * Extract filename from full path
 *
 * @param path - Full file path
 *
 * @returns {string} Filename only
 *
 */
const getFileName = (path: string): string => {
  // Extract just the filename from the path
  const parts = path.split(/[/\\]/);
  return parts.at(-1) ?? path;
};

/**
 * WHAT:
 *   Zeigt für vier Qualitätsmetriken (Comment Density, Modularität, Komplexität, Code-Quality-Issues)
 *   jeweils die Top-N „schlechtesten“ Dateien inklusive Severity-Badge und Ranking an.
 *
 * WHY:
 *   Bietet einen kompakten Überblick über die kritischsten Dateien im Projekt und priorisiert so
 *   Refactoring- und Dokumentationsarbeiten.
 *
 * @returns {JSX.Element} Code quality matrix widget component
 *
 * BEHAVIOR:
 *   - Nutzt `useCodeQualityMatrix`, um pro Metrik Worst-File-Listen, Lade- und Fehlerzustände zu beziehen.
 *   - Formatiert `lastUpdated` für einen menschenlesbaren Zeitstempel.
 *   - Rendert für jede Metrik eine Spalte mit Header, Loading-/Error-States und Dateiliste.
 *
 * RISKS:
 *   - NOTE: Bei leeren Listen werden Platzhaltertexte angezeigt; dies sollte im Analyzer bewusst berücksichtigt werden.
 *   - NOTE: Pfadkürzung (`getFileName`) zeigt nur den Dateinamen, vollständiger Pfad bleibt im Tooltip erhalten.
 *
 */
export const CodeQualityMatrixWidget: FC = () => {
  const { config } = useWidgetConfig(WIDGET_IDS.CODE_QUALITY_MATRIX);
  const { data, hasError, isLoading, refetch } = useCodeQualityMatrix();

  const lastUpdatedText = useMemo(() => {
    if (!data.lastUpdated) {
      return null;
    }
    const date = new Date(data.lastUpdated);
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }, [data.lastUpdated]);

  const metrics = useMemo(
    () => [
      {
        bgColor: "bg-blue-500/10",
        color: "text-blue-400",
        error: data.errors.comment,
        files: data.worstFilesByMetric.comment,
        icon: FileCode,
        id: "comment" as const,
        label: "Kommentare",
        loading: data.loading.comment,
      },
      {
        bgColor: "bg-purple-500/10",
        color: "text-purple-400",
        error: data.errors.modularity,
        files: data.worstFilesByMetric.modularity,
        icon: TrendingDown,
        id: "modularity" as const,
        label: "Modularität",
        loading: data.loading.modularity,
      },
      {
        bgColor: "bg-orange-500/10",
        color: "text-orange-400",
        error: data.errors.complexity,
        files: data.worstFilesByMetric.complexity,
        icon: AlertTriangle,
        id: "complexity" as const,
        label: "Komplexität",
        loading: data.loading.complexity,
      },
      {
        bgColor: "bg-red-500/10",
        color: "text-red-400",
        error: data.errors.issues,
        files: data.worstFilesByMetric.issues,
        icon: Bug,
        id: "issues" as const,
        label: "Code Quality Issues",
        loading: data.loading.issues,
      },
    ],
    [data],
  );

  return (
    <WidgetContainer
      description="Top 3 schlechteste Dateien nach Metrik"
      error={hasError ? "Fehler beim Laden der Code Quality Daten" : null}
      icon={<FileCode className="w-5 h-5" />}
      loading={isLoading}
      onRefresh={refetch}
      size={config?.size ?? "xlarge"}
      title="Code Quality Matrix"
      widgetId={WIDGET_IDS.CODE_QUALITY_MATRIX}
    >
      {/* Last Updated Timestamp */}
      {lastUpdatedText && !isLoading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
          <Clock className="w-3 h-3" />
          <span>Last updated: {lastUpdatedText}</span>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div
              className={cn(
                "flex flex-col space-y-3 p-4 rounded-lg border",
                metric.bgColor,
                "border-slate-600/50",
              )}
              key={metric.id}
            >
              {/* Metric Header */}
              <div className="flex items-center gap-2">
                <Icon className={cn("w-5 h-5", metric.color)} />
                <h3 className="text-sm font-semibold text-white">{metric.label}</h3>
              </div>

              {/* Loading State */}
              {metric.loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                </div>
              ) : null}

              {/* Error State */}
              {metric.error && !metric.loading ? (
                <div className="text-xs text-red-400 py-2">{metric.error}</div>
              ) : null}

              {/* Files List */}
              {!metric.loading && !metric.error && (
                <div className="space-y-2">
                  {metric.files.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">Keine Daten verfügbar</p>
                  ) : (
                    metric.files.map((file) => (
                      <div
                        className="p-2 rounded bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 transition-colors"
                        key={file.path}
                        title={file.path}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="text-xs font-medium text-white flex-1 truncate">
                            {getFileName(file.path)}
                          </span>
                          <Badge
                            className={cn("text-xs px-1.5 py-0.5", getSeverityColor(file.severity))}
                            variant="outline"
                          >
                            {file.displayValue ?? file.value}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">#{file.rank}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </WidgetContainer>
  );
};
